import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { createElement } from 'react';
import { probeSequenceAvailability } from '@/services/sequenceAvailability';

interface BlockedCountryState {
  isBlocked: boolean;
  isLoading: boolean;
}

const BlockedCountryContext = createContext<BlockedCountryState>({
  isBlocked: false,
  isLoading: true,
});

/**
 * Provider — mount once in App (inside BrowserRouter).
 *
 * Behavior is gated by the `VITE_FORCE_SEQUENCE_BLOCKED` feature flag so the
 * new probe-based fallback can be rolled out per environment:
 *   - Flag OFF (default, production today) → skip probe entirely, never
 *     report blocked. Sequence Connect is always shown. This preserves the
 *     pre-rollout behavior.
 *   - Flag ON (dev) → probe Sequence's status endpoint once per page load;
 *     HTTP 451 or any unreachable response means Sequence WaaS can't serve
 *     this client and we fall back to social-only login.
 */
export function BlockedCountryProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BlockedCountryState>({
    isBlocked: false,
    isLoading: true,
  });

  useEffect(() => {
    const check = async () => {
      const { available } = await probeSequenceAvailability();
      setState({ isBlocked: !available, isLoading: false });
    };

    check();
  }, []);

  return createElement(
    BlockedCountryContext.Provider,
    { value: state },
    children,
  );
}

/**
 * Hook — reads from the shared context so multiple components
 * don't each fire their own probe.
 */
export const useBlockedCountry = (): BlockedCountryState => {
  return useContext(BlockedCountryContext);
};
